import { useEffect, useRef } from 'react';

import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import type { ProposalDetail } from '@/lib/proposals/core/types';

import {
  slideoutEntryForAutoOpenTarget,
  useAutoOpenTarget,
} from './useAutoOpenTarget';

/**
 * Auto-opens the first record represented by a "View X" button in the action
 * header when an action is opened on the FULL-PAGE route (`/actions/:id`) —
 * the record panel animates open beside the action so the context the action
 * refers to is on screen immediately.
 *
 * When the header will render NO trigger buttons at all (the trigger fetch
 * settled and every email participant's store resolution settled without an
 * entity), fall back to the proposal's source EMAIL THREAD instead: opening
 * the action auto-opens the email-thread slideout, so an email-sourced
 * action with no CRM records still opens with its context on screen.
 *
 * Route-only behavior, so it lives with the route: ActionDetailPage renders
 * this (null-rendering) component instead of the shared ProposalDetailPanel
 * growing a mode flag. It reuses `useAutoOpenTarget`, the same
 * "what would populate the panel" derivation the persistent-panel gate uses
 * (built on `useTriggerShortcutButtons`, so React Query dedupes the trigger
 * fetch), so "first record" here is always the first button the user sees —
 * same `sortTriggerButtons` order (deal > contact > company > rest).
 *
 * Deliberate limits:
 * - Fires at most ONCE per mount, and the one-shot is consumed ONLY when the
 *   push actually happens (host enabled, stack empty, target present). If the
 *   target settles while the host is disabled (rail open, viewport too narrow)
 *   or the stack is occupied, the attempt is NOT consumed — it stays armed so
 *   that when the host next becomes eligible (e.g. the rail closes) the record
 *   still opens beside the action, matching the persistent panel's skeleton.
 *   Consuming eagerly before the enabled/host checks left the panel stuck as a
 *   permanent skeleton. Once a push has occurred, re-renders never re-fire it.
 *   The host keys this component by action id, so navigating to another action
 *   re-arms it.
 * - `enabled` is the host's business: ActionDetailPage passes
 *   `canHostInline && !isListSidebarOpen && !userClosed`, which excludes mobile
 *   / narrow viewports (where the push would become a full overlay slideout),
 *   the browse-mode actions rail (which competes for the same horizontal
 *   space), and an action the user has explicitly closed on this page (so a
 *   late-resolving target cannot reopen a panel the user just dismissed).
 * - Never stomps a slideout that is already open — if the user (or a
 *   previous action) has something on the stack, the auto-open stands down.
 * - The record opens through the normal slideout stack, so the proposal's
 *   preview projection applies to it exactly as it would on a manual click.
 */
export default function AutoOpenFirstRecord({
  action,
  enabled,
}: {
  action: ProposalDetail;
  /** Whether the host can currently receive the auto-open. Re-checked whenever
   * it changes: a target that settles while `enabled` is `false` waits for it
   * to flip `true` (the one-shot is not consumed until the push happens). */
  enabled: boolean;
}) {
  const result = useAutoOpenTarget(action);
  const slideoutStack = useSlideoutStackContextOptional();
  const pushSlideout = slideoutStack?.pushSlideout;
  const topEntry = slideoutStack?.topEntry;

  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current || result.status === 'resolving') {
      return;
    }
    // Host not ready (rail open, viewport too narrow, or the user closed the
    // panel): do NOT consume the one-shot. Wait for `enabled` to flip true so
    // the record still opens beside the action once the host becomes eligible
    // — otherwise the persistent panel is left showing a skeleton no push ever
    // fills. Consuming here (before the enabled check) was that bug.
    if (!enabled) {
      return;
    }
    // Host IS ready: this is the decision point, so consume the one-shot now —
    // whether or not there is something to push. Settling with no target, or
    // with another slideout already on the stack, is a real "stand down"
    // decision that must not be re-armed by a later re-render.
    firedRef.current = true;
    if (topEntry != null || !pushSlideout || !result.target) {
      return;
    }
    pushSlideout(slideoutEntryForAutoOpenTarget(result.target));
  }, [result, enabled, topEntry, pushSlideout]);

  return null;
}
